import {useState, useEffect, useRef} from "react";
import {App as AntdApp, Spin} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {TextAreaField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetAssistantContextQuery,
	useGetConversationsQuery,
	useGetConversationMessagesQuery,
	useSendAssistantChatMutation,
	useDeleteConversationMutation,
} from "~/reduxs/api/aiApiSlice";
import Markdown from "./Markdown";
import AiKeyAlert from "./AiKeyAlert";
import TokenRemaining from "./TokenRemaining";
import style from "../style/AiTool.module.scss";

const cn = className.bind(style);

const TOOL_LABEL = {seo: 'Chấm điểm SEO', crawl: 'Site Crawler', pagespeed: 'PageSpeed', gsc: 'Search Console'};

/** Gợi ý câu hỏi theo dữ liệu đang có (giúp người dùng bắt đầu). */
function suggestions(available = []) {
	const s = [];
	if (available.includes('crawl')) {
		s.push('Website của tôi đang có những lỗi SEO kỹ thuật nào cần ưu tiên xử lý?');
		s.push('Nhóm điểm SEO nào của tôi đang yếu nhất và cách cải thiện?');
	}
	if (available.includes('gsc')) s.push('Từ khoá nào đang tiềm năng mà tôi nên tối ưu thêm?');
	if (available.includes('pagespeed')) s.push('Làm sao cải thiện Core Web Vitals cho trang của tôi?');
	if (!s.length) s.push('Hãy gợi ý các bước SEO cơ bản nên làm cho website của tôi.');
	return s.slice(0, 4);
}

/**
 * AI SEO Assistant (#30): chatbot phân tích website trong 1 dự án. Mỗi lượt được BE
 * nạp ngữ cảnh dữ liệu thật (crawl/seo/gsc/psi) nên trả lời bám số liệu. Cột trái =
 * danh sách hội thoại; cột phải = khung chat + gợi ý câu hỏi.
 */
function AiAssistant({projectId}) {

	const {notification, modal} = AntdApp.useApp();

	// Tự refetch khi mở lại tab / quay lại cửa sổ → tránh bám trạng thái "chưa cấu hình" cũ bị cache.
	const {data: ctx} = useGetAssistantContextQuery(projectId, {
		refetchOnMountOrArgChange: true,
		refetchOnFocus: true,
		refetchOnReconnect: true,
	});
	const {data: conversations = []} = useGetConversationsQuery(projectId);
	const [convId, setConvId] = useState(0);
	const {data: convData, isFetching: loadingMsgs} = useGetConversationMessagesQuery(convId, {skip: !convId});
	const [sendChat, {isLoading: sending}] = useSendAssistantChatMutation();
	const [deleteConversation] = useDeleteConversationMutation();

	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState('');
	const scrollRef = useRef(null);

	// Nạp tin nhắn khi mở 1 hội thoại có sẵn.
	// RTK Query giữ `data` của hội thoại vừa xem trước đó khi đổi arg (data ≠ currentData),
	// nên phải khớp convData.conversation.id === convId để KHÔNG ghi đè bằng tin của hội thoại cũ
	// (bug: tạo/chọn hội thoại B nhưng hiển thị nội dung hội thoại A).
	// KHÔNG đồng bộ khi đang gửi: refetch-on-focus (alt-tab quay lại) trả bản DB CHƯA có
	// tin nhắn optimistic vừa thêm → ghi đè là mất tin đang gửi.
	useEffect(() => {
		if (convId && !sending && convData?.conversation?.id === convId && convData?.messages) {
			setMessages(convData.messages);
		}
	}, [convId, convData, sending]);

	// Tự cuộn xuống cuối khi có tin mới / đang gửi.
	useEffect(() => {
		if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
	}, [messages, sending]);

	const notConfigured = ctx && !ctx.configured;

	const newChat = () => {
		setConvId(0);
		setMessages([]);
		setInput('');
	};

	const send = async (text) => {
		const content = (text ?? input).trim();
		if (!content || sending) return;
		setInput('');
		setMessages((m) => [...m, {role: 'user', content}]);
		try {
			const res = await sendChat({projectId, message: content, conversation_id: convId}).unwrap();
			setMessages((m) => [...m, {role: 'assistant', content: res.reply, source: res.source}]);
			if (!convId && res.conversation_id) setConvId(res.conversation_id);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không nhận được trả lời')});
			setMessages((m) => m.slice(0, -1)); // gỡ tin user vừa thêm nếu lỗi
		}
	};

	const removeConv = (id, e) => {
		e.stopPropagation();
		modal.confirm({
			title: 'Xoá hội thoại này?',
			okText: 'Xoá',
			okButtonProps: {danger: true},
			cancelText: 'Huỷ',
			onOk: async () => {
				try {
					await deleteConversation(id).unwrap();
					if (id === convId) newChat();
				} catch (err) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(err)});
				}
			},
		});
	};

	return (
		<div className={cn('assistant')}>
			<div className={cn('convList')}>
				<Button primary small onClick={newChat} leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
					Cuộc trò chuyện mới
				</Button>
				<div className={cn('convItems')}>
					{conversations.map((c) => (
						<div key={c.id} className={cn('convItem', {active: c.id === convId})} onClick={() => setConvId(c.id)}>
							<span className={cn('convTitle')}>{c.title}</span>
							<span className={cn('convDel')} onClick={(e) => removeConv(c.id, e)}>
								<FontAwesomeIcon icon="fa-light fa-trash" />
							</span>
						</div>
					))}
					{conversations.length === 0 && <div className={cn('convEmpty')}>Chưa có hội thoại nào</div>}
				</div>
			</div>

			<div className={cn('chat')}>
				{ctx?.available?.length > 0 && (
					<div className={cn('ctxBar')}>
						<FontAwesomeIcon icon="fa-light fa-database" /> Dữ liệu đang dùng:
						{ctx.available.map((a) => <span key={a} className={cn('ctxTag')}>{TOOL_LABEL[a] || a}</span>)}
					</div>
				)}

				{notConfigured && (
					<AiKeyAlert
						configured={false}
						requireOwnKey={Boolean(ctx.require_own_key)}
						provider={ctx.provider}
						style={{margin: '0 0 12px'}}
					/>
				)}

				<div className={cn('messages')} ref={scrollRef}>
					{loadingMsgs && convId ? (
						<div className={cn('loading')}><Spin /></div>
					) : messages.length === 0 ? (
						<div className={cn('welcome')}>
							<FontAwesomeIcon icon="fa-light fa-robot" />
							<p>Xin chào! Tôi là trợ lý SEO cho website này. Hỏi tôi về lỗi SEO, từ khoá, tốc độ trang…</p>
							<div className={cn('suggests')}>
								{suggestions(ctx?.available).map((q, i) => (
									<button key={i} type="button" className={cn('suggest')} onClick={() => send(q)} disabled={notConfigured}>
										{q}
									</button>
								))}
							</div>
						</div>
					) : (
						messages.map((m, i) => (
							<div key={i} className={cn('msg', {user: m.role === 'user', bot: m.role !== 'user'})}>
								<div className={cn('avatar')}>
									<FontAwesomeIcon icon={m.role === 'user' ? 'fa-light fa-user' : 'fa-light fa-robot'} />
								</div>
								<div className={cn('bubble')}>
									{m.role === 'assistant' ? (
										<>
											<Markdown content={m.content} className={cn('md')} />
											{m.source === 'local' && (
												<div className={cn('localTag')}>
													<FontAwesomeIcon icon="fa-light fa-bolt" /> Trả lời từ dữ liệu — không tốn AI
												</div>
											)}
										</>
									) : (
										<div className={cn('userText')}>{m.content}</div>
									)}
								</div>
							</div>
						))
					)}

					{sending && (
						<div className={cn('msg', 'bot')}>
							<div className={cn('avatar')}><FontAwesomeIcon icon="fa-light fa-robot" /></div>
							<div className={cn('bubble')}><Spin size="small" /> <span style={{color: '#888'}}>Đang phân tích…</span></div>
						</div>
					)}
				</div>

				<div className={cn('composerMeta')}>
					<TokenRemaining />
				</div>

				<div className={cn('composer') + ' form'}>
					<TextAreaField
						name="assistant-input"
						value={input}
						onChange={(e) => setInput(e.target.value)}
						placeholder="Nhập câu hỏi về SEO website của bạn… (Enter để gửi, Shift+Enter xuống dòng)"
						autoSize={{minRows: 1, maxRows: 4}}
						allowClear={false}
						onPressEnter={(e) => {
							if (!e.shiftKey) {
								e.preventDefault();
								send();
							}
						}}
						disabled={notConfigured}
					/>
					<Button primary onClick={() => send()} loading={sending} disabled={notConfigured || !input.trim()}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-paper-plane" />}>
						Gửi
					</Button>
				</div>
			</div>
		</div>
	);
}

export default AiAssistant;
